import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SheetUrlField, CustomPositionsField, PositionCoachesField, ShareField } from "@/components/roster/SettingsFields";

function validateCustom(v, customs) {
  if (!v) return "";
  if (v.length > 8) return "Max 8 chars for a custom position";
  if (customs.includes(v)) return "Already added";
  if (customs.length >= 5) return "Up to 5 custom positions";
  return null;
}

export function RosterSettingsDialog({ data, onClose, onSaved }) {
  const cfg = data?.config || {};
  const [url, setUrl] = useState(cfg.sheet_url || "");
  const [customs, setCustoms] = useState(cfg.custom_positions || []);
  const [newCustom, setNewCustom] = useState("");
  const [shared, setShared] = useState(cfg.shared_user_ids || []);
  const [posCoaches, setPosCoaches] = useState(cfg.position_coaches || {});
  const [saving, setSaving] = useState(false);

  const staff = data?.staff || [];
  const standard = data?.positions?.standard || [];
  const shareable = staff.filter(s => s.role !== "head_coach" && s.role !== "athletic_director");
  // Only offer positions actually in use (or already tagged), so the UI doesn't sprawl
  const positionsInUse = useMemo(() => {
    const set = new Set(Object.keys(posCoaches || {}));
    for (const p of (data?.players || [])) if (p.position) set.add(p.position);
    return [...(data?.positions?.standard || []), ...customs].filter(p => set.has(p));
  }, [data, customs, posCoaches]);

  const toggleShare = (id) => setShared(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const addCustom = () => {
    const v = newCustom.trim().toUpperCase();
    const err = validateCustom(v, customs);
    if (err === "") return;
    if (err) return toast.error(err);
    setCustoms([...customs, v]);
    setNewCustom("");
  };
  const removeCustom = (v) => setCustoms(customs.filter(x => x !== v));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/roster/config", {
        sheet_url: url, custom_positions: customs, shared_user_ids: shared, position_coaches: posCoaches,
      });
      toast.success("Roster settings saved");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="settings-dialog">
      <div className="bg-background border border-surface-3 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-surface-3 flex items-center justify-between">
          <div>
            <div className="font-display text-2xl uppercase tracking-wider text-white">Roster Settings</div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-1">Sheet URL · Custom Positions · Coach Access</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="settings-close">✕</button>
        </div>

        <div className="p-6 space-y-6">
          <SheetUrlField url={url} setUrl={setUrl} />
          <CustomPositionsField standard={standard} customs={customs} newCustom={newCustom}
            setNewCustom={setNewCustom} addCustom={addCustom} removeCustom={removeCustom} />
          <PositionCoachesField positionsInUse={positionsInUse} posCoaches={posCoaches}
            setPosCoaches={setPosCoaches} staff={staff} />
          <ShareField shareable={shareable} shared={shared} toggleShare={toggleShare} />
        </div>

        <div className="p-6 border-t border-surface-3 flex justify-end gap-2">
          <Button
            onClick={onClose}
            data-testid="settings-cancel"
            className="rounded-none bg-transparent hover:bg-surface text-slate-300 border border-surface-3 h-10 text-[10px] font-condensed uppercase tracking-widest"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="settings-save"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[10px] font-condensed uppercase tracking-widest"
          >
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
